import React, { createContext, useContext, useState, useEffect } from "react";
import i18n from "../i18n";
import {
  SUPPORTED_LANGUAGE_CODES,
  normalizeLanguageCode,
} from "../constants/supportedLanguages";

const LanguageContext = createContext();

export { SUPPORTED_LANGUAGE_CODES, normalizeLanguageCode };

export const LanguageProvider = ({ children }) => {
  const [userLanguage, setUserLanguage] = useState(
    normalizeLanguageCode(i18n.language) || "da"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [availableLanguages] = useState([
    { code: "bg", name: "Български", flag: "🇧🇬" },
    { code: "cs", name: "Čeština", flag: "🇨🇿" },
    { code: "da", name: "Dansk", flag: "🇩🇰" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "el", name: "Ελληνικά", flag: "🇬🇷" },
    { code: "en", name: "English", flag: "🇺🇸" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "et", name: "Eesti", flag: "🇪🇪" },
    { code: "fi", name: "Suomi", flag: "🇫🇮" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "ga", name: "Gaeilge", flag: "🇮🇪" },
    { code: "hr", name: "Hrvatski", flag: "🇭🇷" },
    { code: "hu", name: "Magyar", flag: "🇭🇺" },
    { code: "id", name: "Bahasa Indonesia", flag: "🇮🇩" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
    { code: "lt", name: "Lietuvių", flag: "🇱🇹" },
    { code: "lv", name: "Latviešu", flag: "🇱🇻" },
    { code: "mt", name: "Malti", flag: "🇲🇹" },
    { code: "nl", name: "Nederlands", flag: "🇳🇱" },
    { code: "pl", name: "Polski", flag: "🇵🇱" },
    { code: "pt", name: "Português", flag: "🇵🇹" },
    { code: "ro", name: "Română", flag: "🇷🇴" },
    { code: "sk", name: "Slovenčina", flag: "🇸🇰" },
    { code: "sl", name: "Slovenščina", flag: "🇸🇮" },
    { code: "sv", name: "Svenska", flag: "🇸🇪" },
  ]);

  // Load user's language preference on app start
  useEffect(() => {
    loadUserLanguagePreference();
  }, []);

  // Listen for language changes from i18n
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      setUserLanguage(lng);
    };

    i18n.on("languageChanged", handleLanguageChanged);

    return () => {
      i18n.off("languageChanged", handleLanguageChanged);
    };
  }, []);

  const loadUserLanguagePreference = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        // If no token, use localStorage preference
        const savedLanguage = normalizeLanguageCode(
          localStorage.getItem("preferredLanguage")
        );
        if (savedLanguage) {
          setUserLanguage(savedLanguage);
          i18n.changeLanguage(savedLanguage);
        }
        return;
      }

      const response = await fetch("/api/accounts/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        const pref = normalizeLanguageCode(userData.preferred_language);
        if (pref) {
          setUserLanguage(pref);
          i18n.changeLanguage(pref);
          localStorage.setItem("preferredLanguage", pref);
        }
      }
    } catch (error) {
      console.error("Failed to load language preference:", error);
      // Fallback to localStorage
      const savedLanguage = normalizeLanguageCode(
        localStorage.getItem("preferredLanguage")
      );
      if (savedLanguage) {
        setUserLanguage(savedLanguage);
        i18n.changeLanguage(savedLanguage);
      }
    }
  };

  const updateLanguagePreference = async (languageCode) => {
    const normalized = normalizeLanguageCode(languageCode) || "en";
    setIsLoading(true);
    try {
      // Update i18n immediately for UI responsiveness
      setUserLanguage(normalized);
      i18n.changeLanguage(normalized);
      localStorage.setItem("preferredLanguage", normalized);

      // Update server preference if user is logged in
      const token = localStorage.getItem("access_token");
      if (token) {
        const response = await fetch("/api/accounts/language-preference/", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ language: normalized }),
        });

        if (!response.ok) {
          throw new Error("Failed to update language preference");
        }
      }

      console.log("Language preference updated successfully");
    } catch (error) {
      console.error("Error updating language preference:", error);
      // Revert on error
      const savedLanguage = normalizeLanguageCode(
        localStorage.getItem("preferredLanguage")
      );
      if (savedLanguage && savedLanguage !== normalized) {
        setUserLanguage(savedLanguage);
        i18n.changeLanguage(savedLanguage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getLanguageInfo = (code) => {
    return (
      availableLanguages.find((lang) => lang.code === code) ||
      availableLanguages[0]
    );
  };

  return (
    <LanguageContext.Provider
      value={{
        userLanguage,
        availableLanguages,
        updateLanguagePreference,
        isLoading,
        getLanguageInfo,
        // Backward compatibility with existing LocaleContext
        locale: userLanguage,
        setLocale: updateLanguagePreference,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export default LanguageContext;
